import 'bootstrap/dist/css/bootstrap.min.css';
import React, { useContext, useEffect, useState } from 'react';
import { ButtonGroup, CloseButton, Container, FloatingLabel, Form, Tab, Tabs } from 'react-bootstrap';
import * as Icon from 'react-bootstrap-icons';
import { LinkContainer } from 'react-router-bootstrap';
import { LoginInfo } from '../App';
import ApiFetch from './ApiFetch';
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from 'react-router-dom';
import { login, verifyTwoFactorCode, resendTwoFactorCode, getAntiPhishingPrefix, getNextSendTime } from "../service/LoginService";
import PasskeyService from '../service/PasskeyService';
import { Button, Chip, TextField, Paper, CircularProgress, Alert, Divider } from '@mui/material';
import { Stack, Grid, Typography, Box } from '@mui/material';
import FingerprintIcon from '@mui/icons-material/Fingerprint';

const RegistOrLogin = (props) => {
    {/* 共通のボタンスタイル */ }
    const buttonSx = {
        width: '100%',
        height: 48,
        backgroundColor: 'white',
        border: '1px solid',
        borderColor: 'grey.300',
        color: 'text.primary',
        textTransform: 'none',
        fontSize: '16px',
        '&:hover': {
            backgroundColor: 'grey.50',
            borderColor: 'grey.400'
        },
        '&.Mui-disabled': {
            backgroundColor: 'grey.100',
            borderColor: 'grey.300',
            color: 'text.disabled'
        }
    };

    {/* 共通のボタン内部レイアウト用コンテナスタイル */ }
    const buttonContentSx = {
        width: '100%',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        px: 3
    };

    {/* アイコン配置用の共通スタイル */ }
    const iconContainerSx = {
        position: 'absolute',
        left: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 24,
        height: 24
    };

    {/* ボタンテキスト用の共通スタイル */ }
    const buttonTextSx = {
        width: '100%',
        textAlign: 'center',
        fontWeight: 500
    };

    async function getRegistOAuth(type) {
        return await new ApiFetch().get(ApiFetch.API_PROTOCOL + "//" + ApiFetch.API_HOST + "/v1/regist/oauth/" + type).then((r) => r.json().then((json) => {
            return json
        }))
    }
    async function getLoginOAuth(type) {
        return await new ApiFetch().get(ApiFetch.API_PROTOCOL + "//" + ApiFetch.API_HOST + "/v1/login/oauth/" + type).then((r) => r.json().then((json) => {
            return json
        }))
    }
    const { t, i18n } = useTranslation()
    // ログイン情報
    const { loginInfo, setLoginInfo } = useContext(LoginInfo)
    const [tabKey, setTabKey] = useState("login")
    const [loading, setLoading] = useState(false)
    const navigate = useNavigate()
    
    // 2要素認証用の状態管理
    const [showTwoFactor, setShowTwoFactor] = useState(false)
    const [twoFactorCode, setTwoFactorCode] = useState('')
    const [twoFactorLoading, setTwoFactorLoading] = useState(false)
    const [resendLoading, setResendLoading] = useState(false)
    const [twoFactorError, setTwoFactorError] = useState('')
    const [userCredentials, setUserCredentials] = useState({ email: '', password: '' })
    const [antiPhishingPrefix, setAntiPhishingPrefix] = useState('')
    
    // 送信制御用の状態管理
    const [canSendNow, setCanSendNow] = useState(true)
    const [remainingSeconds, setRemainingSeconds] = useState(0)
    const [sendLimitMessage, setSendLimitMessage] = useState('')
    
    // パスキー認証用の状態
    const [passkeyLoading, setPasskeyLoading] = useState(false)
    const [passkeyAvailable, setPasskeyAvailable] = useState(false)

    // 2要素認証コード送信処理
    const handleTwoFactorSubmit = async () => {
        if (twoFactorCode.length !== 6) {
            setTwoFactorError('認証コードは6桁で入力してください')
            return
        }

        setTwoFactorLoading(true)
        setTwoFactorError('')

        try {
            const result = await verifyTwoFactorCode(twoFactorCode)
            if (result.result) {
                setLoginInfo(result)
                window.location.reload()
            } else {
                setTwoFactorError(result.message || '認証コードが正しくありません')
            }
        } catch (error) {
            setTwoFactorError('認証に失敗しました。もう一度お試しください。')
        } finally {
            setTwoFactorLoading(false)
        }
    }

    // 送信制御の状態を更新する関数
    const updateSendStatus = async () => {
        try {
            const result = await getNextSendTime()
            if (result.result) {
                setCanSendNow(result.can_send_now)
                setRemainingSeconds(result.remaining_seconds || 0)
                setSendLimitMessage('')
            }
        } catch (error) {
            console.error('送信制御状態の取得に失敗しました:', error)
        }
    }

    // タイマー用のuseEffect
    useEffect(() => {
        let timer
        if (showTwoFactor && remainingSeconds > 0) {
            timer = setInterval(() => {
                setRemainingSeconds(prev => {
                    if (prev <= 1) {
                        setCanSendNow(true)
                        return 0
                    }
                    return prev - 1
                })
            }, 1000)
        }
        return () => clearInterval(timer)
    }, [showTwoFactor, remainingSeconds])

    // 2要素認証が表示された時に送信制御状態を取得
    useEffect(() => {
        if (showTwoFactor) {
            updateSendStatus()
        }
    }, [showTwoFactor])

    // 認証コード再送信処理
    const handleResendCode = async () => {
        if (!canSendNow) {
            return
        }
        
        setResendLoading(true)
        setTwoFactorError('')
        setSendLimitMessage('')

        try {
            const result = await resendTwoFactorCode()
            if (result.result) {
                // 再送信時に新しい英語2文字を取得
                if (result.data && result.data.anti_phishing_prefix) {
                    setAntiPhishingPrefix(result.data.anti_phishing_prefix)
                } else {
                    await fetchAntiPhishingPrefix()
                }
                setTwoFactorError('')
                // 送信制御状態を更新
                await updateSendStatus()
                // 成功メッセージを表示
                alert('認証コードを再送信しました')
            } else {
                if (result.message) {
                    setSendLimitMessage(result.message)
                } else {
                    setTwoFactorError(result.message || '再送信に失敗しました')
                }
            }
        } catch (error) {
            setTwoFactorError('再送信に失敗しました。もう一度お試しください。')
        } finally {
            setResendLoading(false)
        }
    }

    // 6桁数値のみを許可する入力制御（英語2文字は除外）
    const handleTwoFactorCodeChange = (e) => {
        const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6)
        setTwoFactorCode(value)
        if (twoFactorError) setTwoFactorError('')
    }
    
    // パスキー認証が利用可能かチェック
    useEffect(() => {
        const checkPasskeyAvailability = async () => {
            try {
                const hasPasskey = await PasskeyService.hasPasskeyAvailable()
                setPasskeyAvailable(hasPasskey)
            } catch (error) {
                console.error('パスキー確認エラー:', error)
                setPasskeyAvailable(false)
            }
        }
        
        if (tabKey === 'login') {
            checkPasskeyAvailability()
        }
    }, [tabKey])
    
    // パスキーでログイン
    const handlePasskeyLogin = async () => {
        setPasskeyLoading(true)
        try {
            const result = await PasskeyService.authenticate()
            if (result.result) {
                setLoginInfo(result.data)
                window.location.reload()
            } else {
                alert(result.message || 'パスキー認証に失敗しました')
            }
        } catch (error) {
            console.error('パスキーログインエラー:', error)
            alert('パスキー認証に失敗しました')
        } finally {
            setPasskeyLoading(false)
        }
    }
    
    // パスキー登録を促すダイアログを表示
    const checkAndPromptPasskeyRegistration = async () => {
        try {
            // パスキーが利用可能かチェック
            const isAvailable = PasskeyService.isAvailable();
            if (!isAvailable) {
                return true; // パスキー未対応なのでリロード続行
            }
            
            // 既に登録済みかチェック
            const hasPasskey = await PasskeyService.hasPasskeyAvailable();
            if (hasPasskey) {
                return true; // 既に登録済みなのでリロード続行
            }
            
            // パスキー登録を促すダイアログを表示（リロードを遅延）
            setTimeout(() => {
                const shouldRegister = window.confirm(
                    'パスキーを使うと、次回から指紋認証や顔認証で簡単にログインできます。\n\n' +
                    '✓ パスワード入力不要\n' +
                    '✓ より安全な認証\n' +
                    '✓ 素早くログイン\n\n' +
                    '今すぐパスキーを登録しますか？'
                );
                
                if (shouldRegister) {
                    registerPasskey();
                } else {
                    // ユーザーが「後で」を選択したらリロード
                    window.location.reload();
                }
            }, 500);
            
            return false; // ダイアログ表示のためリロードを一時停止
        } catch (error) {
            console.error('パスキー登録促進エラー:', error);
            return true; // エラー時はリロード続行
        }
    };
    
    // パスキー登録を実行
    const registerPasskey = async () => {
        try {
            await PasskeyService.register();
            alert('パスキーの登録が完了しました！次回からパスキー認証でログインできます。');
            window.location.reload();
        } catch (error) {
            console.error('パスキー登録エラー:', error);
            alert(`パスキー登録に失敗しました: ${error.message}`);
            window.location.reload();
        }
    };

    // フィッシング防止用英語2文字を取得
    const fetchAntiPhishingPrefix = async () => {
        try {
            const result = await getAntiPhishingPrefix()
            if (result.result && result.data) {
                setAntiPhishingPrefix(result.data.anti_phishing_prefix || '')
            }
        } catch (error) {
            console.error('英語2文字の取得に失敗しました:', error)
        }
    }

    return (
        <Container>
            <p>登録してAI生成機能を利用したりコメントしよう。</p>
            {!showTwoFactor && (
                <Tabs defaultActiveKey={"login"} activeKey={tabKey} className="mt-3 mb-3 w-100 d-flex" onSelect={(e) => setTabKey(e)}>
                    <Tab eventKey="login" title={t("login.login")}></Tab>
                    <Tab eventKey="regist" title={t("regist.signup")}></Tab>
                </Tabs>
            )}
            {tabKey === "regist" ?
                (

                    <Stack spacing={2} sx={{ width: '100%', maxWidth: 'sm', mx: 'auto', p: 3 }}>

                        {/* Google Signup */}
                        <Button
                            variant="contained"
                            disabled={loading}
                            onClick={() => {
                                setLoading(true);
                                getRegistOAuth("google").then(r => {
                                    if (r.result) {
                                        window.location.href = r.data.url;
                                    } else {
                                        navigate("/regist-select?autherror=1");
                                    }
                                });
                            }}
                            sx={buttonSx}
                        >
                            <Box sx={buttonContentSx}>
                                <Box sx={iconContainerSx}>
                                    <Icon.Google />
                                </Box>
                                <Typography sx={buttonTextSx}>
                                    {t("regist.signupWithGoogle")}
                                </Typography>
                            </Box>
                        </Button>

                        {/* X (Twitter) Signup */}
                        <Button
                            variant="contained"
                            disabled={loading}
                            onClick={() => {
                                setLoading(true);
                                getRegistOAuth("x").then(r => {
                                    if (r.result) {
                                        window.location.href = r.data.url;
                                    } else {
                                        navigate("/regist-select?autherror=1");
                                    }
                                });
                            }}
                            sx={buttonSx}
                        >
                            <Box sx={buttonContentSx}>
                                <Box sx={iconContainerSx}>
                                    <Icon.TwitterX />
                                </Box>
                                <Typography sx={buttonTextSx}>
                                    {t("regist.signupWithTwitterId")}
                                </Typography>
                            </Box>
                        </Button>

                        {/* LINE Signup */}
                        <Button
                            variant="contained"
                            disabled={loading}
                            onClick={() => {
                                setLoading(true);
                                getRegistOAuth("line").then(r => {
                                    if (r.result) {
                                        window.location.href = r.data.url;
                                    } else {
                                        navigate("/regist-select?autherror=1");
                                    }
                                });
                            }}
                            sx={buttonSx}
                        >
                            <Box sx={buttonContentSx}>
                                <Box sx={iconContainerSx}>
                                    <Icon.Line />
                                </Box>
                                <Typography sx={buttonTextSx}>
                                    {t("regist.signupWithLINE")}
                                </Typography>
                            </Box>
                        </Button>

                        {/* OurtAI Signup */}
                        <Link to="/regist" style={{ textDecoration: 'none', width: '100%' }}>
                            <Button
                                variant="contained"
                                sx={buttonSx}
                            >
                                <Box sx={buttonContentSx}>
                                    <Typography sx={buttonTextSx}>
                                        {t("regist.signupWithOurtAI")}
                                    </Typography>
                                </Box>
                            </Button>
                        </Link>

                        {/* World ID Signup */}
                        <Button
                            variant="contained"
                            disabled={loading}
                            onClick={() => {
                                setLoading(true);
                                getRegistOAuth("worldId").then(r => {
                                    if (r.result) {
                                        window.location.href = r.data.url;
                                    } else {
                                        navigate("/regist-select?autherror=1");
                                    }
                                });
                            }}
                            sx={buttonSx}
                        >
                            <Box sx={buttonContentSx}>
                                <Box sx={iconContainerSx}>
                                    <img
                                        src="/img/logos/worldcoin-org-wld-logo.svg"
                                        alt="World ID"
                                        width={24}
                                        height={24}
                                    />
                                </Box>
                                <Typography sx={buttonTextSx}>
                                    World ID
                                </Typography>
                            </Box>
                        </Button>
                    </Stack>) : ""}
            {tabKey === "login" ?
                (<>
                    {!showTwoFactor && (
                        <Container className="mt-3 mb-3 w-100 p-0 text-center">
                            <Form>
                                <FloatingLabel controlId="popup-email" label={t("common.emailOrUserId")} className="mb-3">
                                    <Form.Control type="email" />
                                </FloatingLabel>
                                <FloatingLabel controlId="password" label={t("common.password")} className="mb-3">
                                    <Form.Control type="password" />
                                </FloatingLabel>
                                <Chip disabled={loading} color="primary" onClick={() => {
                                    const email = document.getElementById("popup-email").value
                                    const password = document.getElementById("password").value
                                    
                                    setLoading(true)
                                    setUserCredentials({ email, password })
                                    
                                    login(email, password, props.dialog, setLoginInfo).then(async (r) => {
                                        if (r.success && !r.requiresTwoFactor) {
                                            // パスキー登録を促す（必要に応じてリロードを遅延）
                                            const shouldReload = await checkAndPromptPasskeyRegistration()
                                            if (shouldReload) {
                                                window.location.reload()
                                            }
                                        } else if (r.requiresTwoFactor) {
                                            // 2要素認証が必要 - 英語2文字を取得
                                            if (r.data && r.data.anti_phishing_prefix) {
                                                setAntiPhishingPrefix(r.data.anti_phishing_prefix)
                                            } else {
                                                // バックアップとして別途取得
                                                await fetchAntiPhishingPrefix()
                                            }
                                            // 送信制御状態を初期化
                                            setCanSendNow(true)
                                            setRemainingSeconds(0)
                                            setSendLimitMessage('')
                                            setShowTwoFactor(true)
                                            setLoading(false)
                                        } else {
                                            setLoading(false)
                                        }
                                    })
                                }} label={t("login.login")} />
                                <Link to={"/temporary_password"} style={{ marginLeft: "10px" }}>パスワードを忘れた場合</Link>
                            </Form>
                            
                            {/* パスキー認証ボタン */}
                            {passkeyAvailable && (
                                <>
                                    <Divider sx={{ my: 3 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            または
                                        </Typography>
                                    </Divider>
                                    <Button
                                        variant="outlined"
                                        disabled={passkeyLoading}
                                        onClick={handlePasskeyLogin}
                                        startIcon={passkeyLoading ? <CircularProgress size={20} /> : <FingerprintIcon />}
                                        sx={{
                                            width: '100%',
                                            maxWidth: 400,
                                            py: 1.5,
                                            borderColor: 'primary.main',
                                            '&:hover': {
                                                borderColor: 'primary.dark',
                                                backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                            }
                                        }}
                                    >
                                        パスキーでログイン
                                    </Button>
                                </>
                            )}
                        </Container>
                    )}
                    
                    {/* 2要素認証フォーム */}
                    {showTwoFactor && (
                        <Container className="mt-4 mb-3 w-100 p-0">
                            <Paper elevation={3} sx={{ p: 3, maxWidth: 400, mx: 'auto' }}>
                                <Stack spacing={2} alignItems="center">
                                    <Typography variant="h6" align="center" sx={{ mb: 1 }}>
                                        2要素認証
                                    </Typography>
                                    <Typography variant="body2" align="center" color="text.secondary">
                                        メールアドレスに送信されたコードの数値6桁を入力してください
                                    </Typography>
                                    
                                    {/* フィッシング防止用英語2文字表示 */}
                                    {antiPhishingPrefix && (
                                        <Box sx={{ 
                                            p: 2, 
                                            bgcolor: '#f8f9fa', 
                                            borderRadius: 1,
                                            border: '2px solid',
                                            borderColor: '#dee2e6',
                                            textAlign: 'center',
                                            width: '100%',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                        }}>
                                            <Typography variant="body2" color="#495057" sx={{ mb: 1 }}>
                                                セキュリティコード
                                            </Typography>
                                            <Typography variant="h4" sx={{ 
                                                fontWeight: 'bold',
                                                letterSpacing: '4px',
                                                color: '#212529',
                                                fontFamily: 'monospace',
                                                backgroundColor: '#ffffff',
                                                padding: '8px 16px',
                                                borderRadius: '4px',
                                                border: '1px solid #ced4da',
                                                display: 'inline-block'
                                            }}>
                                                {antiPhishingPrefix}
                                            </Typography>
                                            <Typography variant="caption" color="#6c757d" sx={{ mt: 1, display: 'block' }}>
                                                メール内のコードの最初の2文字と一致することを確認してください
                                            </Typography>
                                        </Box>
                                    )}
                                    
                                    <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 2 }}>
                                        コードの数値部分（6桁）のみ入力
                                    </Typography>
                                    
                                    <TextField
                                        value={twoFactorCode}
                                        onChange={handleTwoFactorCodeChange}
                                        placeholder="123456"
                                        variant="outlined"
                                        inputProps={{
                                            maxLength: 6,
                                            style: { 
                                                textAlign: 'center', 
                                                fontSize: '24px',
                                                letterSpacing: '8px',
                                                fontWeight: 'bold'
                                            }
                                        }}
                                        sx={{
                                            width: '200px',
                                            '& .MuiOutlinedInput-root': {
                                                height: '60px'
                                            }
                                        }}
                                        error={!!twoFactorError}
                                        helperText={twoFactorError}
                                    />
                                    
                                    {/* 送信制限メッセージ */}
                                    {sendLimitMessage && (
                                        <Alert severity="warning" sx={{ width: '100%', mb: 1 }}>
                                            {sendLimitMessage}
                                        </Alert>
                                    )}
                                    
                                    <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                                        <Button
                                            variant="outlined"
                                            onClick={handleResendCode}
                                            disabled={resendLoading || !canSendNow || remainingSeconds > 0}
                                            sx={{ flex: 1 }}
                                        >
                                            {resendLoading ? (
                                                <CircularProgress size={20} />
                                            ) : remainingSeconds > 0 ? (
                                                `再送信 (${remainingSeconds}秒)`
                                            ) : !canSendNow ? (
                                                '送信制限中'
                                            ) : (
                                                '再送信'
                                            )}
                                        </Button>
                                        <Button
                                            variant="contained"
                                            onClick={handleTwoFactorSubmit}
                                            disabled={twoFactorLoading || twoFactorCode.length !== 6}
                                            sx={{ flex: 1 }}
                                        >
                                            {twoFactorLoading ? (
                                                <CircularProgress size={20} />
                                            ) : (
                                                '認証'
                                            )}
                                        </Button>
                                    </Stack>
                                    
                                    <Button
                                        variant="text"
                                        onClick={() => {
                                            setShowTwoFactor(false)
                                            setTwoFactorCode('')
                                            setTwoFactorError('')
                                            setAntiPhishingPrefix('')
                                            setCanSendNow(true)
                                            setRemainingSeconds(0)
                                            setSendLimitMessage('')
                                        }}
                                        sx={{ mt: 1 }}
                                    >
                                        戻る
                                    </Button>
                                </Stack>
                            </Paper>
                        </Container>
                    )}
                    
                    {!showTwoFactor && <hr />}

                    {!showTwoFactor && (
                        <Stack
                            spacing={2}
                            sx={{
                                width: '100%',
                                maxWidth: 'sm',
                                mx: 'auto',
                                p: 1
                            }}
                        >
                            {/* タイトル */}
                            <Typography
                                variant="h6"
                                align="center"
                                sx={{
                                    mb: 1,
                                    fontWeight: 500
                                }}
                            >
                                その他のアカウントでログイン
                            </Typography>



                        {/* Google Login */}
                        <Button
                            variant="contained"
                            disabled={loading}
                            onClick={() => {
                                setLoading(true);
                                getLoginOAuth("google").then(r => {
                                    if (r.result) {
                                        window.location.href = r.data.url;
                                    } else {
                                        navigate("/login-select?autherror=1");
                                    }
                                });
                            }}
                            sx={buttonSx}
                        >
                            <Box sx={buttonContentSx}>
                                <Box sx={iconContainerSx}>
                                    <Icon.Google />
                                </Box>
                                <Typography sx={buttonTextSx}>
                                    {t("login.loginWithGoogle")}
                                </Typography>
                            </Box>
                        </Button>

                        {/* X (旧Twitter) Login */}
                        <Button
                            variant="contained"
                            disabled={loading}
                            onClick={() => {
                                setLoading(true);
                                getLoginOAuth("x").then(r => {
                                    if (r.result) {
                                        window.location.href = r.data.url;
                                    } else {
                                        navigate("/login-select?autherror=1");
                                    }
                                });
                            }}
                            sx={buttonSx}
                        >
                            <Box sx={buttonContentSx}>
                                <Box sx={iconContainerSx}>
                                    <Icon.TwitterX />
                                </Box>
                                <Typography sx={buttonTextSx}>
                                    {t("login.loginWithTwitterId")}
                                </Typography>
                            </Box>
                        </Button>

                        {/* LINE Login */}
                        <Button
                            variant="contained"
                            disabled={loading}
                            onClick={() => {
                                setLoading(true);
                                getLoginOAuth("line").then(r => {
                                    if (r.result) {
                                        window.location.href = r.data.url;
                                    } else {
                                        navigate("/login-select?autherror=1");
                                    }
                                });
                            }}
                            sx={buttonSx}
                        >
                            <Box sx={buttonContentSx}>
                                <Box sx={iconContainerSx}>
                                    <Icon.Line />
                                </Box>
                                <Typography sx={buttonTextSx}>
                                    {t("login.loginWithLINE")}
                                </Typography>
                            </Box>
                        </Button>

                        {/* World ID Login */}
                        <Button
                            variant="contained"
                            disabled={loading}
                            onClick={() => {
                                setLoading(true);
                                getLoginOAuth("worldId").then(r => {
                                    if (r.result) {
                                        window.location.href = r.data.url;
                                    } else {
                                        navigate("/login-select?autherror=1");
                                    }
                                });
                            }}
                            sx={buttonSx}
                        >
                            <Box sx={buttonContentSx}>
                                <Box sx={iconContainerSx}>
                                    <img
                                        src="/img/logos/worldcoin-org-wld-logo.svg"
                                        alt="World ID"
                                        width={24}
                                        height={24}
                                    />
                                </Box>
                                <Typography sx={buttonTextSx}>
                                    World ID
                                </Typography>
                            </Box>
                        </Button>

                        {/* Misskey Login */}
                        <Button
                            variant="contained"
                            disabled={loading}
                            onClick={() => {
                                setLoading(true);
                                getLoginOAuth("misskey").then(r => {
                                    if (r.result) {
                                        window.location.href = r.data.url;
                                    } else {
                                        navigate("/login-select?autherror=1");
                                    }
                                });
                            }}
                            sx={buttonSx}
                        >
                            <Box sx={buttonContentSx}>
                                <Box sx={iconContainerSx}>
                                    <img
                                        src="/img/misskey.png"
                                        alt="Misskey"
                                        width={24}
                                        height={24}
                                    />
                                </Box>
                                <Typography sx={buttonTextSx}>
                                    Misskey
                                </Typography>
                            </Box>
                        </Button>
                    </Stack>
                    )}
                </>) : ""}
            <hr />
            <Container className='text-center mt-3'>
                <Chip label="Terms" onClick={() => navigate("/terms")} /> <Chip label="Privacy Policy" onClick={() => navigate("/privacy")} />
            </Container>
        </Container>
    )
}

export default RegistOrLogin;
